import { StatusDot, type StatusTone } from "@/components/ui";
import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from "react";
import { TOAST_Z_INDEX } from "@/components/layout/interactive-layer";
import { useDesktopToastVisibility } from "@/desktop/use-desktop-toast-visibility";
import { getWindowDisplayId } from "@/desktop/use-window-role";
import { useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { cn } from "@/lib/cn";
import { useOnboardingSystemNoticesBlocked } from "@/lib/onboarding-system-notices";
import { dedupWorkToastsByTask } from "@/lib/work-toast-item";
import { useNotificationStore } from "@/stores/notification-store";
import { useDesktopOnboardingActive } from "@/stores/onboarding-store";
import { isComputerWorkToastPinned } from "@/lib/work-toast-computer-pin";
import {
  useWorkToastStore,
  type WorkToastItem,
} from "@/stores/work-toast-store";
import { SystemToastPill } from "./system-toast-pill";
import { SubagentToastRail } from "./subagent-toast-rail";
import { useComputerUseToastFeed } from "@/desktop/use-computer-use-toast-feed";
import { toastShellAttributes, toastShellClassName } from "./toast-pill";
import {
  buildToastStackEntries,
  systemToastKey,
  type ToastStackEntry,
} from "./toast-stack-entries";
import { isWorkToastVisible } from "./toast-status";
import { useAnsweredQuestion } from "./use-answered-question";
import { useDraggableTop } from "./use-draggable-top";
import { WorkToastAnsweredCard } from "./work-toast-answered-card";
import { WorkToastPill } from "./work-toast-pill";

/** Is any entry still time-bound — a tracked "working" item inside its
 *  window, or an unhovered "done" item before its window closes (spec —
 *  `toast-status.ts`'s `isWorkToastVisible`)? An expanded item is excluded:
 *  it stays visible regardless of state, so it never needs a tick. */
function hasTimeBoundVisibility(
  items: WorkToastItem[],
  hoveredKey: string | null,
  expandedKey: string | null,
): boolean {
  return items.some((item) => {
    if (
      item.dismissed ||
      !item.tracked ||
      item.key === expandedKey ||
      isComputerWorkToastPinned(item)
    )
      return false;
    if (item.state === "working") return true;
    return item.state === "done" && item.key !== hoveredKey;
  });
}

/** Local clock. Ticks only while this window is actually showing the stack
 *  AND at least one entry is time-bound — an all-attention (or empty) stack
 *  keeps no timer pending at all. Never writes to any store.
 *
 *  A single `setTimeout` to the next deadline was tried first, but this
 *  codebase's time-based tests (see `toast-stack.test.tsx`'s "drops an item"
 *  case) simulate elapsed time cheaply via `vi.setSystemTime()` plus a small
 *  `advanceTimersByTime()` — which does not retroactively make an
 *  already-scheduled long timeout fire early, since the fake clock's "now"
 *  and its timer queue are decoupled. A short, conditionally-mounted
 *  interval keeps that convention working while still satisfying the actual
 *  ask (no tick when nothing is time-bound). */
function useToastClock(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1_000);
    return () => window.clearInterval(interval);
  }, [active]);
  return now;
}

function ToastStackRow({
  testId,
  label,
  hint,
  dots,
  onClick,
}: {
  testId: string;
  label: string;
  hint: string;
  dots: StatusTone[];
  /** Overflow rows reveal hidden notices or expand the next work item. */
  onClick?: () => void;
}) {
  const className = cn(
    toastShellClassName,
    "denker-toast-width flex h-[30px] shrink-0 items-center gap-2 rounded-[12px] px-2.5 text-appkit-small text-secondary",
  );
  const content = (
    <>
      <span className="flex gap-0.5">
        {dots.map((dot, index) => (
          <StatusDot key={`${dot}-${index}`} tone={dot} />
        ))}
      </span>
      <span className="min-w-0 truncate">{label}</span>
      {hint && <span className="ml-auto shrink-0 text-muted">{hint}</span>}
    </>
  );
  if (!onClick) {
    return (
      <div className={className} data-testid={testId}>
        {content}
      </div>
    );
  }
  return (
    <button
      type="button"
      className={className}
      data-testid={testId}
      data-interactive="true"
      {...toastShellAttributes}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

/** Every toast on screen: work pills, system pills, one open card, "+N" rows. */
export function ToastStack({
  showWork = true,
}: { showWork?: boolean } = {}): ReactElement | null {
  const t = useT(toastsMessages);
  const items = useWorkToastStore((state) => state.items);
  const expandedWorkKey = useWorkToastStore((state) => state.expandedKey);
  const expandedDisplayId = useWorkToastStore(
    (state) => state.expandedDisplayId,
  );
  const hoveredKey = useWorkToastStore((state) => state.hoveredKey);
  const systemToasts = useNotificationStore((state) => state.systemToasts);
  const onboardingActive = useDesktopOnboardingActive();
  const systemNoticesBlocked = useOnboardingSystemNoticesBlocked();
  useComputerUseToastFeed();
  const [expandedSystemId, setExpandedSystemId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  const regularDisplayVisible = useDesktopToastVisibility(
    showWork && expandedWorkKey ? expandedDisplayId : null,
  );
  const shouldRender = regularDisplayVisible && !onboardingActive;
  const itemList = useMemo(
    () =>
      showWork
        ? Object.values(items).filter(
            (item) =>
              item.source !== "computer" && !isComputerWorkToastPinned(item),
          )
        : [],
    [items, showWork],
  );
  const visibleItemMap = useMemo(
    () => Object.fromEntries(itemList.map((item) => [item.key, item])),
    [itemList],
  );
  // Ordinary subagent results retain the existing right-edge rail. App and
  // computer-use activity belongs to the tray toast beneath the menu icon.
  const railBadgeItems = useMemo(
    () =>
      dedupWorkToastsByTask(
        itemList.filter(
          (item) =>
            item.source === "native_worker" && item.tracked && !item.dismissed,
        ),
      ).sort((a, b) => b.stateChangedAt - a.stateChangedAt),
    [itemList],
  );
  const stackSourceItems = useMemo(
    () =>
      dedupWorkToastsByTask(
        itemList.filter((item) => item.source !== "native_worker"),
      ),
    [itemList],
  );
  // Same flag the visibility gate uses: only the window actually showing the
  // stack may run the local clock or write the "answered" timeout back to
  // the (mirrored) store.
  const active = shouldRender;
  const now = useToastClock(
    active && hasTimeBoundVisibility(itemList, hoveredKey, expandedWorkKey),
  );
  const { answered, clear: clearAnswered } = useAnsweredQuestion(
    visibleItemMap,
    showWork ? expandedWorkKey : null,
    active,
  );
  // Right-edge rail: minimal pills for subagent + computer-use work. Pills
  // flash on begin/done and hide while running (the old toast behavior);
  // badges persist until pruned. Filtered here where `now` is available.
  const railPills = useMemo(
    () =>
      railBadgeItems.filter((item) =>
        isWorkToastVisible(item, now, {
          hovered: hoveredKey === item.key,
          expanded: expandedWorkKey === item.key,
        }),
      ),
    [railBadgeItems, now, hoveredKey, expandedWorkKey],
  );

  // One card at a time: a work card and a system card never coexist.
  useEffect(() => {
    if (showWork && expandedWorkKey) setExpandedSystemId(null);
  }, [expandedWorkKey, showWork]);
  useEffect(() => {
    if (
      expandedSystemId &&
      !systemToasts.some((toast) => toast.id === expandedSystemId)
    ) {
      setExpandedSystemId(null);
    }
  }, [expandedSystemId, systemToasts]);

  // Stable across renders (id-based, not an inline closure) so the memoized
  // SystemToastPill doesn't re-render just because its neighbors did.
  const handleSystemExpand = useCallback(
    (id: string) => {
      if (showWork) useWorkToastStore.getState().collapse();
      setExpandedSystemId(id);
    },
    [showWork],
  );
  const handleSystemCollapse = useCallback(() => setExpandedSystemId(null), []);

  if (!shouldRender) return null;

  const displayId = getWindowDisplayId();
  const visibleItems = stackSourceItems.filter((item) =>
    isWorkToastVisible(item, now, {
      hovered: hoveredKey === item.key,
      expanded: expandedWorkKey === item.key,
    }),
  );
  const expandedKey = expandedSystemId
    ? systemToastKey(expandedSystemId)
    : showWork
      ? expandedWorkKey
      : null;
  const entries = buildToastStackEntries({
    items: visibleItems,
    systemToasts: systemNoticesBlocked ? [] : systemToasts,
    expandedKey,
    showAll,
  });
  const answeredIsSubagent = answered?.source === "native_worker";
  const answeredInEntries =
    answered && !answeredIsSubagent
      ? entries.some(
          (entry) => entry.kind === "work" && entry.key === answered.key,
        )
      : false;
  if (
    entries.length === 0 &&
    (!answered || answeredIsSubagent) &&
    railBadgeItems.length === 0
  )
    return null;

  const renderEntry = (entry: ToastStackEntry) => {
    switch (entry.kind) {
      case "work":
        return answered && entry.key === answered.key ? (
          <WorkToastAnsweredCard
            key={entry.key}
            item={answered}
            onDone={clearAnswered}
          />
        ) : (
          <WorkToastPill
            key={entry.key}
            item={entry.item}
            expanded={entry.key === expandedWorkKey}
            displayId={displayId}
          />
        );
      case "system":
        return (
          <SystemToastPill
            key={entry.key}
            toast={entry.toast}
            expanded={entry.toast.id === expandedSystemId}
            onExpand={handleSystemExpand}
            onCollapse={handleSystemCollapse}
          />
        );
      case "need-you":
        return (
          <ToastStackRow
            key={entry.key}
            testId="toast-stack-need-you"
            label={t("stackNeedYou", { count: entry.count })}
            hint={t("stackOpen")}
            dots={entry.dots}
            onClick={() =>
              useWorkToastStore.getState().expand(entry.oldestKey, displayId)
            }
          />
        );
      case "collapse":
        return (
          <ToastStackRow
            key={entry.key}
            testId="toast-stack-less"
            label={t("stackShowLess")}
            hint=""
            dots={[]}
            onClick={() => setShowAll(false)}
          />
        );
      case "more": {
        // Under an open card, "+N more" only ever closes it back to the pill
        // row. With nothing open, system overflow reveals the full list;
        // work-only overflow keeps its direct work-item shortcut.
        const onClick = expandedKey
          ? () => {
              useWorkToastStore.getState().collapse();
              setExpandedSystemId(null);
            }
          : entry.revealAll
            ? () => setShowAll(true)
            : entry.expandKey
              ? () =>
                  useWorkToastStore
                    .getState()
                    .expand(entry.expandKey!, displayId)
              : undefined;
        return (
          <ToastStackRow
            key={entry.key}
            testId="toast-stack-more"
            label={t("stackMore", { count: entry.count })}
            hint={onClick ? t("stackShow") : ""}
            dots={entry.dots}
            onClick={onClick}
          />
        );
      }
    }
  };

  return (
    <ToastStackColumn
      entries={entries}
      answered={answered}
      answeredInEntries={answeredInEntries}
      answeredIsSubagent={answeredIsSubagent}
      clearAnswered={clearAnswered}
      renderEntry={renderEntry}
      railPills={railPills}
      railBadgeItems={railBadgeItems}
      displayId={displayId}
      expandedWorkKey={
        displayId === null || expandedDisplayId === displayId
          ? expandedWorkKey
          : null
      }
    />
  );
}

/**
 * The single right-edge toast column: stack entries (pills, system toasts,
 * answered cards) on top, rail pills below. One container means the surfaces
 * can never overlap. The whole column drags vertically via its grip.
 */
function ToastStackColumn({
  entries,
  answered,
  answeredInEntries,
  answeredIsSubagent,
  clearAnswered,
  renderEntry,
  railPills,
  railBadgeItems,
  displayId,
  expandedWorkKey,
}: {
  entries: ToastStackEntry[];
  answered: WorkToastItem | null;
  answeredInEntries: boolean;
  answeredIsSubagent: boolean;
  clearAnswered: () => void;
  renderEntry: (entry: ToastStackEntry) => ReactNode;
  railPills: WorkToastItem[];
  railBadgeItems: WorkToastItem[];
  displayId: number | null;
  expandedWorkKey: string | null;
}) {
  const { top, gripHandlers, takeDragged } = useDraggableTop();
  const drag = { gripHandlers, takeDragged };
  const showStack =
    entries.length > 0 ||
    (answered && !answeredInEntries && !answeredIsSubagent);
  if (!showStack && railBadgeItems.length === 0) return null;
  return (
    <div
      className="pointer-events-none fixed right-4 flex max-h-[calc(100vh-2rem)] flex-col items-end gap-2 overflow-y-auto"
      style={{
        zIndex: TOAST_Z_INDEX,
        top,
        maxHeight: `calc(100vh - ${top}px - 16px)`,
      }}
      data-testid="toast-stack"
    >
      {showStack && (
        <>
          {answered && !answeredInEntries && !answeredIsSubagent && (
            <WorkToastAnsweredCard item={answered} onDone={clearAnswered} />
          )}
          {entries.map(renderEntry)}
        </>
      )}
      <SubagentToastRail
        pillItems={railPills}
        hasItems={railBadgeItems.length > 0}
        displayId={displayId}
        expandedKey={expandedWorkKey}
        drag={drag}
      />
    </div>
  );
}
